import * as cfe from 'ego-cfe';
import * as api from 'ego-sdk-js';
import React from 'react';

import useApiDo from './hooks/useApiDo';
import CircleQuestionIcon from './icon/CircleQuestion';
import CloseCircleIcon from './icon/CloseCircleIcon';
import PlusIcon from './icon/PlusIcon';
import VerifiedAuthorIcon from './icon/VerifiedAuthorIcon';
import Button from './lib/Button';
import Collapse from './lib/Collapse';
import InputLabel from './lib/InputLabel';
import ListGroup from './lib/ListGroup';
import Modal from './lib/Modal';
import Spinner from './lib/Spinner';
import TextInput from './lib/TextInput';
import ToolTip from './lib/ToolTip';
import SectionHeaderTab from './SectionHeaderTab';
import { useSmartGoToFeed } from './SmartFeedLink';

const AuthorSection = (props: {
  goToFeed: (feed: api.feed.IFeedInfo) => void;
  apiClient: api.SuperegoClient;
  authors: cfe.ApiData.Data<api.stimulus.ICreatorReference[]>;
  showAgentUI: boolean;
  url: string;
  refreshStimulus: () => void;
}) => {
  const weightedAuthors = cfe.ApiData.hasData(props.authors)
    ? props.authors.data.filter(author => author['.tag'] === 'linked' || author['.tag'] === 'unlinked')
    : [];
  const totalWeight = weightedAuthors.reduce((acc, author) => acc + author.weight, 0);
  const smartGoToFeed = useSmartGoToFeed(props.goToFeed, props.apiClient);
  const authorsWithCreators =
    cfe.ApiData.getData(props.authors)?.filter(
      author => author['.tag'] === 'linked' || author['.tag'] === 'unlinked',
    ) || [];
  return (
    <div className="tw-mt-4">
      <SectionHeaderTab title="Authors" round="right" />
      <div className="tw-mb-2" />
      <div>
        {cfe.ApiData.isLoading(props.authors) ? (
          <div className="kb-pad-4-na">
            <Spinner />
          </div>
        ) : cfe.ApiData.hasData(props.authors) ? (
          props.authors.data.length === 0 ? (
            <ToolTip placement="left" overlay={<div>Unclaimed</div>}>
              <span className="kb-pad-4-na">
                &mdash; <span className="tw-ml-1" />
                <CircleQuestionIcon size="1rem" />
              </span>
            </ToolTip>
          ) : (
            <ListGroup>
              {authorsWithCreators.map(author => (
                <AuthorItem
                  showAgentUI={props.showAgentUI}
                  apiClient={props.apiClient}
                  smartGoToFeed={smartGoToFeed}
                  author={author}
                  totalWeight={totalWeight}
                  url={props.url}
                  refreshStimulus={props.refreshStimulus}
                />
              ))}
            </ListGroup>
          )
        ) : cfe.ApiData.isError(props.authors) ? (
          <span className="kb-pad-4-na">Uh oh, unexpected error.</span>
        ) : null}
      </div>
      {props.showAgentUI ? (
        <>
          <AddLinkedAuthorForm apiClient={props.apiClient} url={props.url} refreshStimulus={props.refreshStimulus} />
          <AddUnlinkedAuthorForm apiClient={props.apiClient} url={props.url} refreshStimulus={props.refreshStimulus} />
        </>
      ) : null}
    </div>
  );
};

const AuthorItem = (props: {
  showAgentUI: boolean;
  apiClient: api.SuperegoClient;
  smartGoToFeed: (feed: string) => void;
  author: api.stimulus.ILinkedCreatorReference | api.stimulus.IUnlinkedCreatorReference;
  totalWeight: number;
  url: string;
  refreshStimulus: () => void;
}) => {
  const {
    apiDo: apiStaffOpStimulusUncreditCreator,
    errToast,
    okToast,
  } = useApiDo(props.apiClient, props.apiClient.staffOpStimulusUncreditCreator);
  const { apiDo: apiStaffOpStimulusCreditChangeWeight } = useApiDo(
    props.apiClient,
    props.apiClient.staffOpStimulusCreditChangeWeight,
  );
  const [weightEditMode, setWeightEditMode] = React.useState(false);
  const [newWeight, setNewWeight] = React.useState<number>(props.author.weight);
  return (
    <>
      <ListGroup.Item
        key={props.author['.tag'] === 'linked' ? props.author.linked_user.user_id : props.author.creator_id}
        className="!tw-py-1 tw-cursor-default"
        mutedHover
        onClick={
          props.author['.tag'] === 'linked'
            ? () => props.author['.tag'] === 'linked' && props.smartGoToFeed('u/' + props.author.linked_user.user_id)
            : undefined
        }
      >
        <div className="tw-grow tw-flex tw-justify-between tw-gap-x-2">
          <span>
            {props.author.name ? <>{props.author.name} &bull; </> : null}
            {props.author.role}
            {props.author['.tag'] === 'linked' ? (
              <span title="Verified Superego user">
                <VerifiedAuthorIcon size="1rem" offsetUp className="tw-ml-1" />
              </span>
            ) : null}
          </span>
          <div className="tw-flex tw-gap-x-2">
            {props.author['.tag'] === 'linked' || props.author['.tag'] === 'unlinked' ? (
              <span title="The share of ink this author receives">
                {props.showAgentUI ? (
                  <>
                    w={props.author.weight}{' '}
                    <Button
                      variant="primary"
                      sm
                      mimicDisabled={weightEditMode}
                      onClick={e => {
                        e.stopPropagation();
                        setWeightEditMode(!weightEditMode);
                      }}
                    >
                      edit
                    </Button>
                  </>
                ) : null}
                {props.totalWeight > 0 ? (
                  <>
                    {props.showAgentUI ? <> &bull; </> : null}
                    {Math.round((props.author.weight / props.totalWeight) * 100)}%
                  </>
                ) : null}
              </span>
            ) : null}
            {props.showAgentUI && (props.author['.tag'] === 'linked' || props.author['.tag'] === 'unlinked') ? (
              <Button
                variant="danger"
                sm
                onClick={e => {
                  e.stopPropagation();
                  apiStaffOpStimulusUncreditCreator(
                    {
                      creator_id: props.author.creator_id,
                      url: props.url,
                    },
                    {
                      onResult: () => {
                        okToast('Author uncredited');
                        props.refreshStimulus();
                      },
                      onRouteErr(err, defaultErrToast) {
                        if (err['.tag'] === 'cannot_uncredit_hgc_author') {
                          errToast('Cannot uncredit (HGC author). Set weight to 0 instead.');
                        } else {
                          defaultErrToast();
                        }
                      },
                    },
                  );
                }}
              >
                uncredit
              </Button>
            ) : null}
          </div>
        </div>
      </ListGroup.Item>
      {weightEditMode ? (
        <div className="tw-pl-8 tw-pr-6 tw-flex tw-items-center tw-gap-x-2" onClick={e => e.stopPropagation()}>
          ↳
          <TextInput
            type="text"
            sm
            placeholder="New weight"
            inputMode="numeric"
            value={newWeight.toString()}
            onChange={e => {
              const v = e.currentTarget.value;
              // Accept only whole numbers; ignore any input that would produce NaN
              if (v === '') {
                setNewWeight(0);
              } else if (/^\d+$/.test(v)) {
                setNewWeight(parseInt(v, 10));
              }
            }}
          />
          <Button
            variant="primary"
            sm
            onClick={e => {
              e.stopPropagation();
              setWeightEditMode(true);
              apiStaffOpStimulusCreditChangeWeight(
                {
                  creator_id: props.author.creator_id,
                  url: props.url,
                  weight: newWeight,
                },
                {
                  onResult: () => {
                    okToast('Weight updated');
                    props.refreshStimulus();
                  },
                },
              );
            }}
          >
            set new weight
          </Button>
        </div>
      ) : null}
    </>
  );
};

const AddLinkedAuthorForm = (props: { apiClient: api.SuperegoClient; url: string; refreshStimulus: () => void }) => {
  const [showForm, setShowForm] = React.useState(false);
  const [addAuthorUsername, setAddAuthorUsername] = React.useState('');
  const [selectedAuthor, setSelectedAuthor] = React.useState<api.user.IUserInfo | null>(null);
  const [addAuthorRole, setAddAuthorRole] = React.useState<string>('');
  const [addAuthorWeight, setAddAuthorWeight] = React.useState(1);
  const [searchRes, setSearchRes] = React.useState<cfe.ApiData.Data<api.search.IQueryAuthorsResult>>({
    kind: 'unknown',
  });
  const asyncSearchIdRef = React.useRef<ReturnType<typeof setTimeout>>();

  const onUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.currentTarget.value;
    setAddAuthorUsername(q);
    if (asyncSearchIdRef.current !== undefined) {
      clearTimeout(asyncSearchIdRef.current);
    }
    if (q.length < 3) {
      return;
    }
    setSearchRes({ kind: 'loading' });
    asyncSearchIdRef.current = setTimeout(() => {
      asyncSearchIdRef.current = undefined;
      props.apiClient
        .searchQueryAuthors({ q })
        .then(resp => {
          if (resp.kind === api.StatusCode.Ok) {
            setSearchRes({ kind: 'loaded', data: resp.result });
          } else {
            setSearchRes({ kind: 'error' });
          }
        })
        .catch(() => {
          setSearchRes({ kind: 'error' });
        });
    }, 250);
  };

  const {
    apiDo: apiStaffOpStimulusCreditUser,
    okToast,
    errToast,
  } = useApiDo(props.apiClient, props.apiClient.staffOpStimulusCreditUser);
  return (
    <div>
      <div className="kb-pad-4">
        <Button sm className="tw-mt-1" onClick={() => setShowForm(!showForm)}>
          <PlusIcon size="1rem" className="tw-mr-1" /> Credit linked author
        </Button>
      </div>
      <Collapse open={showForm}>
        <div className="tw-px-4 tw-py-4 tw-mt-2 tw-bg-highlight">
          <div className="tw-mb-4 tw-w-full">
            <Modal.Heading4>Credit Superego author account</Modal.Heading4>
            <p>
              A <i>linked author</i> has a Superego account that's also registered (hence <i>linked</i>) as an author
              account.
            </p>
            <InputLabel>Search by username</InputLabel>
            <TextInput
              type="text"
              placeholder="Username"
              value={addAuthorUsername}
              onChange={onUsernameChange}
              suffixEle={cfe.ApiData.isLoading(searchRes) ? <Spinner sm /> : null}
            />
            <div className="tw-text-muted">Only author accounts are shown.</div>
            {cfe.ApiData.hasData(searchRes) ? (
              <ListGroup>
                {searchRes.data.matches.map(match => (
                  <ListGroup.Item
                    key={match.user_id}
                    className="!tw-py-1"
                    mutedHover
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedAuthor(match);
                      setAddAuthorUsername('');
                      setSearchRes({ kind: 'unknown' });
                    }}
                  >
                    <span className="tw-mr-2 tw-font-semibold">{match.name}</span>
                    <span className="tw-text-muted">@{match.username}</span>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : null}
          </div>
          <div className="tw-mb-4">
            <InputLabel>Selected Author</InputLabel>
            {selectedAuthor ? (
              <div>
                {selectedAuthor.name} &bull; @{selectedAuthor.username}{' '}
                <span role="button" onClick={() => setSelectedAuthor(null)}>
                  <CloseCircleIcon size="1.25rem" offsetUp />
                </span>
              </div>
            ) : (
              <div className="tw-italic tw-text-muted">No author selected</div>
            )}
          </div>
          <div className="tw-mb-4">
            <InputLabel>Role</InputLabel>
            <TextInput
              type="text"
              value={addAuthorRole}
              onChange={e => {
                setAddAuthorRole(e.currentTarget.value);
              }}
            />
          </div>
          <div className="tw-mb-4">
            <InputLabel>Weight</InputLabel>
            <TextInput
              type="text"
              value={addAuthorWeight.toString()}
              onChange={e => {
                setAddAuthorWeight(parseInt(e.currentTarget.value, 10));
              }}
            />
          </div>
          <Button
            variant="primary"
            disabled={!selectedAuthor || addAuthorRole.length === 0}
            onClick={() =>
              apiStaffOpStimulusCreditUser(
                {
                  role: addAuthorRole,
                  url: props.url,
                  user_id: selectedAuthor ? selectedAuthor.user_id : '',
                  weight: addAuthorWeight,
                },
                {
                  onResult: () => {
                    okToast('Author credited');
                    setShowForm(false);
                    setAddAuthorUsername('');
                    setSelectedAuthor(null);
                    setAddAuthorRole('');
                    setAddAuthorWeight(1);
                    props.refreshStimulus();
                  },
                  onRouteErr: err => errToast(err['.tag']),
                },
              )
            }
          >
            Credit Author
          </Button>
        </div>
      </Collapse>
    </div>
  );
};

const AddUnlinkedAuthorForm = (props: { apiClient: api.SuperegoClient; url: string; refreshStimulus: () => void }) => {
  const [showForm, setShowForm] = React.useState(false);
  const [addAuthorRole, setAddAuthorRole] = React.useState<string>('');
  const [addAuthorWeight, setAddAuthorWeight] = React.useState(1);

  const {
    apiDo: apiStaffOpStimulusCreditNewCreator,
    okToast,
    errToast,
  } = useApiDo(props.apiClient, props.apiClient.staffOpStimulusCreditNewCreator);
  return (
    <div>
      <div className="kb-pad-4">
        <Button sm className="tw-mt-1" onClick={() => setShowForm(!showForm)}>
          <PlusIcon size="1rem" className="tw-mr-1" /> Credit placeholder author
        </Button>
      </div>
      <Collapse open={showForm}>
        <div className="tw-px-4 tw-py-4 tw-mt-2 tw-bg-highlight">
          <div className="tw-mb-4 tw-w-full">
            <Modal.Heading4>Credit a placeholder author</Modal.Heading4>
            <p>
              An placeholder aka <i>unlinked author</i> represents a role that deserves a share of the credit, but isn't
              linked yet to a Superego author account.
            </p>
            <p>
              The author will need to join Superego and open an author account. If they do not, their weighted share of
              ink will be forfeited and redistributed among the other authors.
            </p>
          </div>
          <div className="tw-mb-4">
            <InputLabel>Role</InputLabel>
            <TextInput
              type="text"
              value={addAuthorRole}
              onChange={e => {
                setAddAuthorRole(e.currentTarget.value);
              }}
            />
          </div>
          <div className="tw-mb-4">
            <InputLabel>Weight</InputLabel>
            <TextInput
              type="text"
              value={addAuthorWeight.toString()}
              onChange={e => {
                setAddAuthorWeight(parseInt(e.currentTarget.value, 10));
              }}
            />
          </div>
          <Button
            variant="primary"
            disabled={addAuthorRole.length === 0}
            onClick={() =>
              apiStaffOpStimulusCreditNewCreator(
                {
                  role: addAuthorRole,
                  tags: [],
                  url: props.url,
                  weight: addAuthorWeight,
                },
                {
                  onResult: () => {
                    okToast('Author credited');
                    setShowForm(false);
                    setAddAuthorRole('');
                    setAddAuthorWeight(1);
                    props.refreshStimulus();
                  },
                  onRouteErr: err => errToast(err['.tag']),
                },
              )
            }
          >
            Credit Author
          </Button>
        </div>
      </Collapse>
    </div>
  );
};

export default AuthorSection;
